import { escape } from 'lodash-es'
import { mdiTrayArrowDown } from '@mdi/js'

const FONT = 'Roboto, sans-serif'
const INK = '#181818'
const INK_SECONDARY = '#3E3E3C'
const MUTED = '#706E6B'

// "1 set", "2 sets", "3 fixes"
export function formatCount(count, noun) {
  const value = count ?? 0
  const plural = /(s|x|z|ch|sh)$/.test(noun) ? `${noun}es` : `${noun}s`
  return `${value.toLocaleString()} ${value === 1 ? noun : plural}`
}

// ApexCharts renders this as HTML and rule names are user input, so escape them
function tooltipHtml({ color, value, name, sub }) {
  const line = (text, color) =>
    `<div style="padding-left:20px;font-size:12px;color:${color}">${escape(text)}</div>`

  return (
    `<div style="padding:8px 12px;font-family:${FONT}">` +
    `<div style="display:flex;align-items:center;gap:8px;font-size:14px;font-weight:700;color:${INK}">` +
    `<span style="flex:none;width:12px;height:3px;border-radius:2px;background:${color}"></span>` +
    `${escape(value)}</div>` +
    line(name, INK_SECONDARY) +
    (sub ? line(sub, MUTED) : '') +
    '</div>'
  )
}

const DOWNLOAD_ICON = `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="${mdiTrayArrowDown}"/></svg>`

const MAX_LABEL_LINES = 3
const MIN_LABEL_CHARS = 1
const ELLIPSIS = '...'
// room the download icon takes to the right of the title
const TOOLBAR_WIDTH = 44

// shortens to maxWidth, measuring the ellipsis too; setText changes the visible text
function fitText(element, text, maxWidth, forceEllipsis, setText) {
  setText(text)
  // a hidden chart has no characters to measure (measuring would throw); its next render fits it
  if (!element.getNumberOfChars()) return
  const width = element.getComputedTextLength()
  if (!forceEllipsis && width <= maxWidth) return

  setText(text + ELLIPSIS)
  const ellipsisWidth = element.getComputedTextLength() - width
  // binary search for the longest prefix that still fits with the ellipsis
  let low = MIN_LABEL_CHARS
  let high = text.length
  while (low < high) {
    const mid = Math.ceil((low + high) / 2)
    if (element.getSubStringLength(0, mid) + ellipsisWidth <= maxWidth) {
      low = mid
    } else {
      high = mid - 1
    }
  }
  setText(text.slice(0, low) + ELLIPSIS)
}

// on screen only, rebuilt from the full names each time (the CSV and hover title keep them)
function fitLabels({ el, w }) {
  const maxWidth = w.globals.gridWidth / w.globals.labels.length - 4
  if (!(maxWidth > 0)) return
  el.querySelectorAll('.apexcharts-xaxis-label').forEach((label, index) => {
    const original = [w.globals.labels[index] ?? []].flat().map(String)
    const lines = [...label.querySelectorAll('tspan')]
    lines.slice(MAX_LABEL_LINES).forEach(line => line.remove())
    lines.slice(0, MAX_LABEL_LINES).forEach((line, i) => {
      const capped =
        i === MAX_LABEL_LINES - 1 && original.length > MAX_LABEL_LINES
      fitText(
        line,
        original[i] ?? line.textContent,
        maxWidth,
        capped,
        value => {
          line.textContent = value
        },
      )
    })
  })
}

// a rule name as the card title stays on one line, clear of the download icon
function fitTitle({ el, w }) {
  const title = el.querySelector('.apexcharts-title-text')
  const text = w.config.title.text
  if (!title || !text) return
  const target = title.querySelector('tspan') ?? title
  const textNode = [...target.childNodes].find(
    node => node.nodeType === Node.TEXT_NODE,
  )
  const maxWidth =
    w.globals.svgWidth - Number(title.getAttribute('x') || 0) - TOOLBAR_WIDTH
  if (!textNode || !(maxWidth > 0)) return

  if (!title.querySelector('title')) {
    const hover = document.createElementNS(w.globals.SVGNS, 'title')
    hover.textContent = text
    title.append(hover)
  }
  fitText(target, text, maxWidth, false, value => {
    textNode.nodeValue = value
  })
}

function fitChartText(chart) {
  fitLabels(chart)
  fitTitle(chart)
}

// charts drawn while Roboto is still loading, each waiting for one more fit
const awaitingFonts = new WeakSet()

function setAttributes(element, attributes = {}) {
  for (const [name, value] of Object.entries(attributes)) {
    element.setAttribute(name, value)
  }
}

// automation hooks on ApexCharts' SVG: per bar (by data index) and per x-axis label
function tagElements(el, { bars = [], labels = [] } = {}) {
  for (const bar of el.querySelectorAll('path.apexcharts-bar-area')) {
    setAttributes(bar, bars[Number(bar.getAttribute('j'))])
  }
  el.querySelectorAll('.apexcharts-xaxis-label').forEach((label, index) =>
    setAttributes(label, labels[index]),
  )
}

// whole-number ticks with headroom, so the tallest bar's label stays above it
function valueAxis(values) {
  const target = Math.max(0, ...values) * 1.15
  const rough = target / 5
  const magnitude = 10 ** Math.floor(Math.log10(rough || 1))
  const step =
    [1, 2, 2.5, 5, 10]
      .map(factor => factor * magnitude)
      .filter(Number.isInteger)
      .find(candidate => candidate >= rough) ?? 1
  const max = Math.max(step, Math.ceil(target / step) * step)
  return { min: 0, max, tickAmount: max / step }
}

// for text drawn as point annotations above a bar
export function annotationLabelStyle({ muted = false } = {}) {
  return {
    background: 'transparent',
    color: muted ? MUTED : INK,
    fontFamily: FONT,
    fontSize: muted ? '11px' : '12px',
    fontWeight: muted ? 400 : 500,
  }
}

// shared look for the bar charts; unit is singular, sub a string or index => string
export function barChartOptions({
  categories,
  names = categories,
  values,
  unit,
  sub,
  colors,
  title,
  testAttributes,
}) {
  // ApexCharts redraws its SVG on every update, so both run after each render
  const onRender = chart => {
    fitChartText(chart)
    tagElements(chart.el, testAttributes)
    // widths measured before Roboto loads are off; fit once more when it has
    if (document.fonts?.status === 'loading' && !awaitingFonts.has(chart)) {
      awaitingFonts.add(chart)
      document.fonts.ready.then(() => {
        awaitingFonts.delete(chart)
        // skipped if the chart was destroyed meanwhile
        if (chart.w.globals.dom.baseEl) fitChartText(chart)
      })
    }
  }

  return {
    ...(title && {
      title: {
        text: title,
        style: {
          fontFamily: FONT,
          fontSize: '15px',
          fontWeight: 500,
          color: INK,
        },
      },
    }),
    chart: {
      height: 265,
      type: 'bar',
      fontFamily: FONT,
      foreColor: MUTED,
      toolbar: {
        tools: { download: DOWNLOAD_ICON },
      },
      events: { mounted: onRender, updated: onRender },
    },
    colors,
    plotOptions: {
      bar: {
        distributed: true,
        columnWidth: 32,
        borderRadius: 4,
        borderRadiusApplication: 'end',
        dataLabels: { position: 'top' },
      },
    },
    dataLabels: {
      enabled: true,
      offsetY: -20,
      style: { fontSize: '12px', fontWeight: 500, colors: [INK] },
    },
    grid: {
      borderColor: '#ECEBE9',
      xaxis: { lines: { show: false } },
      // trim drops ApexCharts' own right padding, so the last bar's numbers need room
      padding: { right: 24 },
    },
    legend: { show: false },
    xaxis: {
      categories,
      axisBorder: { color: '#DDDBDA' },
      axisTicks: { show: false },
      // names stack onto more lines as bars get closer, so never rotate
      labels: {
        rotate: 0,
        hideOverlappingLabels: false,
        // room for three lines; fitLabels shortens longer names to match
        maxHeight: 64,
        // only to skip the side padding sized from the longest full name; fitLabels redoes the cut
        trim: true,
        style: { colors: INK_SECONDARY, fontSize: '12px' },
      },
    },
    yaxis: {
      ...valueAxis(values),
      labels: {
        style: { colors: MUTED, fontSize: '11px' },
        formatter: val => Math.round(val).toLocaleString(),
      },
    },
    tooltip: {
      // hovering anywhere in a bar's column opens its tooltip, so a tiny bar
      // next to a tall one is still easy to reach
      shared: true,
      intersect: false,
      // ApexCharts writes its default title and series name as HTML; skip both
      x: { show: false },
      y: { title: { formatter: () => '' } },
      custom: ({ dataPointIndex: index }) =>
        tooltipHtml({
          color: colors[index],
          value: formatCount(values[index], unit),
          name: names[index],
          sub: typeof sub === 'function' ? sub(index) : sub,
        }),
    },
  }
}
